// Shared "show a preview, then More+" truncation used by the Community product
// overview and by the ProductDescription panel on Plan/QMI pages (MTTMY-2189).
// Both surfaces must truncate identically, so the logic lives here rather than
// being copied per component.

// Strips tags and decodes the handful of entities the DOM path used to decode.
// Runs identically in Node and the browser, which is what keeps SSR and
// hydration in agreement.
export const getPlainText = (html) =>
  typeof html === 'string'
    ? html
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
    : '';

// Character budgets for the preview, by breakpoint. Mirrors what Community has
// shipped with; Plan/QMI reuse the same numbers so the two read the same.
export const getMaxCharacterCount = (breakpoint) => {
  if (['sm', 'xs'].includes(breakpoint)) return 250;
  if (breakpoint === 'md') return 280;
  if (breakpoint === 'lg') return 300;
  return 340;
};

// Elements that have no children and take no end tag. They matter here because
// a stray `</br>` is not ignored: the HTML parser turns it into a SECOND <br>,
// so emitting `<br></br>` silently doubles every line break in the preview.
const VOID_ELEMENTS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
]);

// Truncates rich text to maxLength *visible* characters while keeping the
// surrounding markup balanced. Walks the DOM, so it cannot run on the server —
// callers must fall back to getPlainText() until they have mounted.
export const truncateHTMLContent = (html, maxLength) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  let textLength = 0;
  let result = '';

  function traverseNodes(node) {
    if (textLength >= maxLength) return;

    const childNodes = node.childNodes;
    for (let i = 0; i < childNodes.length; i++) {
      const child = childNodes[i];

      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent;
        const remainingLength = maxLength - textLength;

        if (textLength + text.length <= maxLength) {
          result += text;
          textLength += text.length;
        } else {
          result += text.substring(0, remainingLength) + '...';
          textLength = maxLength;
          return;
        }
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const tagName = child.tagName.toLowerCase();
        const openTag = `<${tagName}`;
        const attributes = Array.from(child.attributes)
          .map((attr) => ` ${attr.name}="${attr.value}"`)
          .join('');

        const closeOpenTag = `>`;

        result += openTag + attributes + closeOpenTag;

        if (!VOID_ELEMENTS.has(tagName)) {
          traverseNodes(child);

          result += `</${tagName}>`;
        }

        if (textLength >= maxLength) return;
      }
    }
  }

  traverseNodes(tempDiv);
  return result;
};
